import { Button, CircularProgress, Paper, Typography } from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import { useCallback, useState } from 'react';
import { FileParameter } from '@/api/ApiClient';
import { useSnackbar } from '@/hooks/useSnackbar';
import { log } from '@/lib/utils/logger';
import { getProblemDetail } from '@/lib/utils/apiErrors';

interface RecipeFileUploadAreaProps {
  activityId: number;
  recipeTemplate: string;
  onUpload: (file: FileParameter) => Promise<void>;
  disabled?: boolean;
  multiple?: boolean;
}

/**
 * Prominent upload area for recipe file templates (sources includes Upload).
 * Shown in the main detail when a recipe file should be uploaded and none exists yet.
 * Calls POST …/sample-activities/{id}/files/upload-recipe (strict parse), not the Files tab attach route.
 */
export function RecipeFileUploadArea({
  activityId: _activityId,
  recipeTemplate: _recipeTemplate,
  onUpload,
  disabled = false,
  multiple = false,
}: RecipeFileUploadAreaProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const { showSnackbar } = useSnackbar();

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;
      const toUpload = multiple ? files : files.slice(0, 1);
      setIsUploading(true);
      try {
        for (const file of toUpload) {
          await onUpload({ data: file, fileName: file.name });
        }
        showSnackbar(
          toUpload.length === 1
            ? `Successfully uploaded ${toUpload[0].name}`
            : `Successfully uploaded ${toUpload.length} file(s)`,
          'success'
        );
      } catch (error) {
        log.debug('[RecipeFileUploadArea] Upload failed:', error);
        showSnackbar(getProblemDetail(error) ?? 'Upload failed', 'error');
      } finally {
        setIsUploading(false);
      }
    },
    [onUpload, multiple, showSnackbar]
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      e.target.value = '';
      uploadFiles(files);
    },
    [uploadFiles]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only clear if we're leaving the drop zone (not entering a child)
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      if (disabled || isUploading) return;
      const files = Array.from(e.dataTransfer.files || []);
      uploadFiles(files);
    },
    [disabled, isUploading, uploadFiles]
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 4,
        mt: 2,
        textAlign: 'center',
        borderStyle: isDragging ? 'dashed' : 'solid',
        borderWidth: isDragging ? 2 : 1,
        borderColor: isDragging ? 'primary.main' : undefined,
        bgcolor: isDragging ? 'action.hover' : undefined,
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Upload a recipe file (or drag and drop) to populate the table, or enter the data manually below.
      </Typography>
      <Button
        variant="contained"
        component="label"
        startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />}
        disabled={disabled || isUploading}
      >
        {isUploading ? 'Uploading...' : 'Upload recipe file'}
        <input type="file" multiple={multiple} hidden onChange={handleFileChange} />
      </Button>
    </Paper>
  );
}
